// Pure helpers for #Conditional tag values — `<value> @ (<condition>)`, as carried
// by the new-Lanes-spec keys `hov:lanes:conditional` and
// `hov:minimum:lanes:conditional` (per-lane pipe lists whose SLOTS are conditional
// values, e.g. `yes @ (Mo-Fr 05:00-09:00,15:00-19:00)|||`).
//
// No OpenLayers imports so this is unit-testable with `node --test`. The split
// helpers work on one SLOT at a time — splitting the pipe list into slots is
// laneBands' splitLaneList's job, and a time domain never contains `|`, so the two
// splits cannot interfere.
//
// The time-domain evaluator covers the "sharp" subset the Orbis spec recommends and
// the SDLR data actually uses: weekday lists/ranges plus hh:mm-hh:mm ranges,
// multiple rules separated by `;`. Anything it does not understand (months, public
// holidays, fuzzy domains like "dusk-dawn", `off` exclusions) makes parseTimeDomain
// return null — callers must then say "conditional, window not evaluated" rather
// than guessing.

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// `<value> @ (<condition>)` — the value must be non-empty and the condition sits in
// parentheses per the spec's syntax for conditional restrictions. The spec also
// allows multiple `;`-separated <value> @ <condition> pairs in one tag value; in a
// per-lane slot that form is rare and a `;` there is equally a step-section
// separator, so this parser handles exactly one pair and callers treat anything
// else as opaque.
const CONDITIONAL_VALUE = /^\s*([^@]*?)\s*@\s*\(\s*(.*?)\s*\)\s*$/;

/**
 * Split one conditional slot value into its base value and its condition.
 * `"yes @ (Mo-Fr 05:00-09:00)"` → `{ value: "yes", condition: "Mo-Fr 05:00-09:00" }`.
 * A plain value without `@` comes back as `{ value, condition: undefined }`, so
 * callers can feed either encoding through the same path.
 * @param {unknown} raw
 * @returns {{value: string, condition: string|undefined}|undefined} undefined for
 *   empty/blank slots ("nothing said about this lane")
 */
export function splitConditionalValue(raw) {
  if (raw === undefined || raw === null) return undefined;
  const text = String(raw).trim();
  if (text === "") return undefined;
  const match = CONDITIONAL_VALUE.exec(text);
  if (match && match[1] !== "") {
    return { value: match[1], condition: match[2] };
  }
  return { value: text, condition: undefined };
}

/**
 * Is this whole tag value (a pipe list of slots) conditional in at least one slot?
 * Works on unsplit values so per-way styles can test cheaply.
 * @param {unknown} raw
 * @returns {boolean}
 */
export function hasConditionalSlot(raw) {
  if (typeof raw !== "string") return false;
  return raw
    .split("|")
    .some((slot) => splitConditionalValue(slot)?.condition !== undefined);
}

/**
 * Parse a sharp time domain into evaluatable rules.
 *
 * Supported per `;`-separated rule: an optional weekday part (`Mo-Fr`, `Mo,We,Fr`,
 * `Sa-Su` including week wrap `Fr-Mo`) and an optional list of `hh:mm-hh:mm`
 * ranges (overnight ranges like `22:00-06:00` wrap midnight). A rule with only
 * days means all day; only times means every day.
 * @param {string|undefined} condition
 * @returns {{days: boolean[], ranges: {fromMin: number, toMin: number}[]}[]|null}
 *   null when any part of the condition is outside the supported grammar —
 *   callers must treat that as "cannot evaluate", not as never/always.
 */
export function parseTimeDomain(condition) {
  if (typeof condition !== "string" || condition.trim() === "") return null;
  const rules = [];
  for (const ruleText of condition.split(";")) {
    const tokens = ruleText.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return null;
    const days = new Array(7).fill(false);
    let sawDays = false;
    const ranges = [];
    for (const token of tokens) {
      if (parseDayToken(token, days)) {
        sawDays = true;
      } else if (!parseTimeToken(token, ranges)) {
        return null;
      }
    }
    if (!sawDays) days.fill(true);
    rules.push({ days, ranges });
  }
  return rules.length > 0 ? rules : null;
}

// `Mo-Fr`, `Sa`, `Mo,We,Fr`, `Fr-Mo` (wrapping). Returns false when the token is
// not a day expression at all; throws nothing.
function parseDayToken(token, days) {
  const parts = token.split(",");
  const parsed = [];
  for (const part of parts) {
    const range = part.split("-");
    if (range.length > 2) return false;
    const from = DAY_NAMES.indexOf(range[0]);
    const to = range.length === 2 ? DAY_NAMES.indexOf(range[1]) : from;
    if (from < 0 || to < 0) return false;
    parsed.push([from, to]);
  }
  for (const [from, to] of parsed) {
    for (let d = from; ; d = (d + 1) % 7) {
      days[d] = true;
      if (d === to) break;
    }
  }
  return true;
}

// `05:00-09:00,15:00-19:00` — one token can carry several comma-separated ranges.
// `24:00` is accepted as an end ("until midnight"). Returns false on anything else.
function parseTimeToken(token, ranges) {
  const parts = token.split(",");
  const parsed = [];
  for (const part of parts) {
    const match = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/.exec(part);
    if (!match) return false;
    const fromMin = Number(match[1]) * 60 + Number(match[2]);
    const toMin = Number(match[3]) * 60 + Number(match[4]);
    if (
      Number(match[1]) > 24 ||
      Number(match[3]) > 24 ||
      Number(match[2]) > 59 ||
      Number(match[4]) > 59 ||
      fromMin > 24 * 60 ||
      toMin > 24 * 60
    ) {
      return false;
    }
    parsed.push({ fromMin, toMin });
  }
  ranges.push(...parsed);
  return true;
}

/**
 * Evaluate parsed rules at a Date, in that Date's LOCAL time. The restriction's
 * clock times are local to the ROAD, so evaluating at the viewer's clock is only
 * meaningful when the viewer and the road share a timezone — callers must label
 * the result with the timezone they evaluated in.
 * @param {ReturnType<typeof parseTimeDomain>} rules
 * @param {Date} date
 * @returns {boolean|null} null when rules is null (unsupported grammar)
 */
export function isActiveAt(rules, date) {
  if (!rules) return null;
  const day = date.getDay();
  const minutes = date.getHours() * 60 + date.getMinutes();
  const previousDay = (day + 6) % 7;
  for (const rule of rules) {
    if (rule.ranges.length === 0) {
      if (rule.days[day]) return true;
      continue;
    }
    for (const { fromMin, toMin } of rule.ranges) {
      if (fromMin <= toMin) {
        if (rule.days[day] && minutes >= fromMin && minutes < toMin)
          return true;
      } else {
        // Overnight range: the stretch before midnight belongs to the rule's day,
        // the stretch after midnight to the following day.
        if (rule.days[day] && minutes >= fromMin) return true;
        if (rule.days[previousDay] && minutes < toMin) return true;
      }
    }
  }
  return false;
}
